import { createFileRoute } from '@tanstack/react-router'
import { requirePermission } from '@/lib/permissions/permissions'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import LoadingSpinner from '@/components/LoadingSpinner'
import {
  Hourglass,
  MoreVertical,
  Shield,
  UserPlus,
  RotateCcw,
  Trash2,
  UserCog,
  UserMinus,
} from 'lucide-react'
import { MemberPermissionsDialog } from '@/components/team/MemberPermissionsDialog'
import { RolePermissionsDialog } from '@/components/team/RolePermissionsDialog'
import { useHasPermission, useUserId } from '@/lib/states/session/hooks'
import { useOrgEntitlements } from '@/lib/hooks/useOrgEntitlements'
import {
  canInviteCoach,
  parsePlanLimitError,
  PLAN_LABELS,
} from '@/lib/billing/plans'
import { Link } from '@tanstack/react-router'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils } from '@/lib/trpc/vanilla'
import { patchListCache, prependToList, removeFromList } from '@trainrr/api-client'

type TeamInviteRow = Awaited<
  ReturnType<(typeof trpcUtils)['team']['listInvites']['ensureData']>
>[number]

export const Route = createFileRoute('/_auth/_dashboard/team')({
  beforeLoad: () => {
    requirePermission('members.read')
  },
  component: TeamPage,
  loader: async () => {
    await trpcUtils.team.listMembers.ensureData()
  },
})

function TeamPage() {
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteActionId, setInviteActionId] = useState<string | null>(null)
  const [inviteAction, setInviteAction] = useState<'resend' | 'revoke' | null>(
    null,
  )
  const [rolePermissionsOpen, setRolePermissionsOpen] = useState(false)
  const [activeMember, setActiveMember] = useState<{
    userId: string
    name: string
    roleKey: string | null
  } | null>(null)
  const [memberToRemove, setMemberToRemove] = useState<{
    userId: string
    name: string
  } | null>(null)
  const [removingMember, setRemovingMember] = useState(false)

  const utils = trpc.useUtils()
  const inviteCoach = trpc.team.inviteCoach.useMutation()
  const revokeInvite = trpc.team.revokeInvite.useMutation()
  const removeMember = trpc.team.removeMember.useMutation()
  const { data: membersData, isLoading } = trpc.team.listMembers.useQuery()
  const members = membersData ?? []
  const currentUserId = useUserId()
  const currentUserRoleKey =
    members.find((member) => member.user?.user_id === currentUserId)
      ?.role_key ?? null
  const isCurrentUserOwner = currentUserRoleKey === 'owner'

  const canInvite = useHasPermission('members.invite')
  const canEditRoles = useHasPermission('members.edit_roles')
  const canRemoveMembers = useHasPermission('members.remove')
  const { data: entitlements } = useOrgEntitlements()
  const inviteAllowedByPlan = canInviteCoach(entitlements)
  const requiredSeatPlan = entitlements?.required_plan_for_seat_limit

  const {
    data: invitesData,
    isLoading: invitesLoading,
    error: invitesError,
  } = trpc.team.listInvites.useQuery(undefined, { enabled: canInvite })
  const invites = invitesData ?? []
  if (invitesError) {
    console.error('Failed to fetch invites', invitesError)
  }

  const inviteSchema = z.object({
    email: z.string().email('Enter a valid email'),
    role: z.enum(['admin', 'coach']),
  })

  const inviteForm = useForm<z.infer<typeof inviteSchema>>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: '',
      role: 'coach',
    },
  })

  const handleInvite = async (values: z.infer<typeof inviteSchema>) => {
    if (!inviteAllowedByPlan) {
      toast.error('Coach seat limit reached for this plan.')
      return
    }
    if (!canInvite) {
      toast.error('Only admins can invite coaches.')
      return
    }

    setInviteLoading(true)
    try {
      const created = await inviteCoach.mutateAsync({
        email: values.email,
        roleKey: values.role,
      })
      patchListCache(utils.team.listInvites, (old) =>
        prependToList(
          old.filter((row) => row.email !== created.email),
          created,
        ),
      )
      toast.success('Invite sent')
      inviteForm.reset()
      setInviteOpen(false)
    } catch (error) {
      let description: string | undefined
      const planLimit = parsePlanLimitError(error)
      if (planLimit) {
        const nextPlan =
          planLimit.required_plan_key === 'team_monthly'
            ? 'team_monthly'
            : 'growth_monthly'
        description = `Upgrade to ${
          planLimit.required_plan_key
            ? PLAN_LABELS[planLimit.required_plan_key]
            : 'a higher tier'
        } to invite more coaches.`
        window.location.href = `/upgrade?plan=${nextPlan}`
      } else if (error instanceof Error) {
        description = error.message
      }
      console.error('Invite failed', error, description)
      toast.error('Invite failed', { description })
    } finally {
      setInviteLoading(false)
    }
  }

  const handleResendInvite = async (invite: TeamInviteRow) => {
    if (!inviteAllowedByPlan) {
      toast.error('Coach seat limit reached for this plan.')
      return
    }
    if (!canInvite) return
    setInviteActionId(invite.id)
    setInviteAction('resend')
    try {
      const created = await inviteCoach.mutateAsync({
        email: invite.email,
        roleKey: invite.role_key === 'admin' ? 'admin' : 'coach',
      })
      patchListCache(utils.team.listInvites, (old) =>
        prependToList(
          old.filter((row) => row.email !== created.email),
          created,
        ),
      )
      toast.success('Invite resent')
    } catch (error) {
      const planLimit = parsePlanLimitError(error)
      const description = planLimit
        ? `Upgrade to ${
            planLimit.required_plan_key
              ? PLAN_LABELS[planLimit.required_plan_key]
              : 'a higher tier'
          } to invite more coaches.`
        : error instanceof Error
          ? error.message
          : undefined
      console.error('Resend failed', error, description)
      toast.error('Resend failed', { description })
    } finally {
      setInviteActionId(null)
      setInviteAction(null)
    }
  }

  const handleRevokeInvite = async (invite: TeamInviteRow) => {
    if (!canInvite) return
    setInviteActionId(invite.id)
    setInviteAction('revoke')
    try {
      await revokeInvite.mutateAsync({ inviteId: invite.id })
      toast.success('Invite revoked')
      patchListCache(utils.team.listInvites, (old) =>
        removeFromList(old, invite.id),
      )
    } catch (error) {
      const description =
        error instanceof Error ? error.message : undefined
      console.error('Revoke failed', error, description)
      toast.error('Revoke failed', { description })
    } finally {
      setInviteActionId(null)
      setInviteAction(null)
    }
  }

  const handleRemoveMember = async () => {
    if (!memberToRemove || !canRemoveMembers) return
    setRemovingMember(true)
    try {
      await removeMember.mutateAsync({ userId: memberToRemove.userId })
      const current = utils.team.listMembers.getData()
      if (current) {
        utils.team.listMembers.setData(
          undefined,
          current.filter(
            (member) => member.user?.user_id !== memberToRemove.userId,
          ),
        )
      } else {
        void utils.team.listMembers.invalidate()
      }
      await utils.billing.entitlements.invalidate()
      toast.success(`${memberToRemove.name} removed from the organisation`)
      setMemberToRemove(null)
    } catch (error) {
      const description =
        error instanceof Error ? error.message : undefined
      console.error('Remove member failed', error, description)
      toast.error('Failed to remove team member', { description })
    } finally {
      setRemovingMember(false)
    }
  }

  return (
    <div className="w-full">
      {canEditRoles && (
        <MemberPermissionsDialog
          member={activeMember}
          onClose={() => setActiveMember(null)}
        />
      )}
      {canEditRoles && (
        <RolePermissionsDialog
          open={rolePermissionsOpen}
          onOpenChange={setRolePermissionsOpen}
        />
      )}
      <AlertDialog
        open={memberToRemove !== null}
        onOpenChange={(open) => {
          if (!open && !removingMember) {
            setMemberToRemove(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {memberToRemove?.name} from this organisation?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They will lose access to this organisation, its clients, and
              conversations. You can invite them again later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingMember}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={removingMember}
              onClick={(event) => {
                event.preventDefault()
                void handleRemoveMember()
              }}
            >
              {removingMember ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {canInvite && (
        <Dialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          analyticsName="team_invite_dialog"
        >
          <DialogContent className="max-w-md p-0">
            <div className="border-b px-6 py-4">
              <DialogHeader className="text-left">
                <DialogTitle className="text-xl">Invite Coach</DialogTitle>
              </DialogHeader>
            </div>
            <div className="px-6 py-6">
              <Form {...inviteForm}>
                <form
                  onSubmit={inviteForm.handleSubmit(handleInvite)}
                  className="space-y-5"
                >
                  <FormField
                    control={inviteForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input placeholder="coach@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={inviteForm.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Select Role</FormLabel>
                        <FormControl>
                          <RadioGroup
                            className="grid gap-3"
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <label
                              htmlFor="role-coach"
                              className={`flex items-start gap-3 rounded-xl border px-4 py-3 transition ${
                                field.value === 'coach'
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border'
                              }`}
                            >
                              <RadioGroupItem
                                id="role-coach"
                                value="coach"
                                className="mt-1"
                              />
                              <div>
                                <div className="font-medium">Coach</div>
                                <div className="text-xs text-muted-foreground">
                                  Can manage clients and workouts.
                                </div>
                              </div>
                            </label>
                            <label
                              htmlFor="role-admin"
                              className={`flex items-start gap-3 rounded-xl border px-4 py-3 transition ${
                                field.value === 'admin'
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border'
                              }`}
                            >
                              <RadioGroupItem
                                id="role-admin"
                                value="admin"
                                className="mt-1"
                              />
                              <div>
                                <div className="font-medium">Admin</div>
                                <div className="text-xs text-muted-foreground">
                                  Can manage team members and settings.
                                </div>
                              </div>
                            </label>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="space-y-3">
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={inviteLoading}
                    >
                      Send Invite
                      {inviteLoading && <LoadingSpinner className="ml-2" />}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      onClick={() => setInviteOpen(false)}
                      disabled={inviteLoading}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
            <div className="border-t px-6 py-4 text-center text-xs text-muted-foreground">
              Invites expire in 7 days. You can revoke invites from the pending
              tab.
            </div>
          </DialogContent>
        </Dialog>
      )}
      <div className="flex-col md:flex">
        <div className="flex-1 space-y-4 px-4 pt-6 md:p-8">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-3xl font-bold tracking-tight">Team</h2>
            {(canInvite || canEditRoles) && (
              <div className="flex items-center gap-2">
                {canEditRoles && (
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => setRolePermissionsOpen(true)}
                  >
                    <Shield className="h-4 w-4" />
                    Permissions
                  </Button>
                )}
                {canInvite && (
                  <Button
                    className="gap-2"
                    onClick={() => setInviteOpen(true)}
                    disabled={!inviteAllowedByPlan}
                  >
                    <UserPlus className="h-4 w-4" />
                    Invite coach
                  </Button>
                )}
              </div>
            )}
          </div>
          {canInvite && !inviteAllowedByPlan && (
            <Card>
              <CardContent className="flex flex-col gap-2 p-4 text-sm">
                <p className="font-medium">Coach seat limit reached</p>
                <p className="text-muted-foreground">
                  Upgrade to{' '}
                  {requiredSeatPlan
                    ? PLAN_LABELS[requiredSeatPlan]
                    : 'a higher tier'}{' '}
                  to invite more coaches.
                </p>
                <div>
                  <Button asChild size="sm">
                    <Link
                      to="/upgrade"
                      search={{
                        plan:
                          requiredSeatPlan === 'team_monthly'
                            ? 'team_monthly'
                            : 'growth_monthly',
                      }}
                    >
                      View upgrade options
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="border-b">
              <CardTitle>Coaches</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading && (
                <div className="space-y-2 p-6">
                  <div className="h-12 rounded-md bg-muted animate-pulse" />
                  <div className="h-12 rounded-md bg-muted animate-pulse" />
                </div>
              )}

              {!isLoading && members.length === 0 && (
                <p className="p-6 text-sm text-muted-foreground">
                  No team members found.
                </p>
              )}

              {!isLoading &&
                members.map((member, index) => {
                  const user = member.user
                  const fullName =
                    `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()
                  const avatarUrl = user?.avatar?.url
                  const memberUserId = user?.user_id
                  const canEditThisMember =
                    canEditRoles &&
                    memberUserId !== currentUserId &&
                    (member.role_key !== 'owner' || isCurrentUserOwner)
                  const canRemoveThisMember =
                    canRemoveMembers &&
                    memberUserId !== currentUserId &&
                    member.role_key !== 'owner'
                  return (
                    <div
                      key={`${member.role_key}-${user?.user_id}`}
                      className={`flex items-center justify-between px-6 py-4 ${
                        index === members.length - 1 ? '' : 'border-b'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-11 w-11">
                          <AvatarImage
                            src={avatarUrl}
                            className="object-cover"
                          />
                          <AvatarFallback>
                            {user?.first_name?.[0] ?? 'T'}
                            {user?.last_name?.[0] ?? 'R'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-sm">
                            {fullName || user?.email}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {user?.email}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge
                          variant="secondary"
                          className="uppercase text-[10px]"
                        >
                          {member.role_key === 'owner'
                            ? 'Owner'
                            : member.role_key}
                        </Badge>
                        {canEditThisMember || canRemoveThisMember ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  disabled={
                                    removingMember &&
                                    memberToRemove?.userId === memberUserId
                                  }
                                >
                                  <MoreVertical className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canEditThisMember ? (
                                  <DropdownMenuItem
                                    onSelect={() =>
                                      setActiveMember({
                                        userId: memberUserId ?? '',
                                        name:
                                          fullName || user?.email || 'Coach',
                                        roleKey: member.role_key ?? 'coach',
                                      })
                                    }
                                  >
                                    <UserCog className="h-4 w-4" />
                                    Edit permissions
                                  </DropdownMenuItem>
                                ) : null}
                                {canEditThisMember && canRemoveThisMember ? (
                                  <DropdownMenuSeparator />
                                ) : null}
                                {canRemoveThisMember ? (
                                  <DropdownMenuItem
                                    variant="destructive"
                                    onSelect={() =>
                                      setMemberToRemove({
                                        userId: memberUserId ?? '',
                                        name:
                                          fullName || user?.email || 'Coach',
                                      })
                                    }
                                  >
                                    <UserMinus className="h-4 w-4" />
                                    Remove from organisation
                                  </DropdownMenuItem>
                                ) : null}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                      </div>
                    </div>
                  )
                })}
            </CardContent>
          </Card>

          {canInvite && (
            <Card>
              <CardHeader className="flex items-center justify-between border-b flex-row">
                <CardTitle>Pending Invites</CardTitle>
                <Badge variant="secondary">{invites.length} Total</Badge>
              </CardHeader>
              <CardContent className="p-0">
                {invites.length === 0 && !invitesLoading && (
                  <p className="p-6 text-sm text-muted-foreground">
                    No pending invites.
                  </p>
                )}
                {invites.map((invite, index) => (
                  <div
                    key={invite.id}
                    className={`flex items-center justify-between px-6 py-4 ${
                      index === invites.length - 1 ? '' : 'border-b'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full border border-dashed border-muted-foreground/40 text-xs text-muted-foreground">
                        <Hourglass className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">
                          {invite.email}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Invited on{' '}
                          {new Date(invite.created_at).toLocaleDateString(
                            'en-US',
                            {
                              month: 'short',
                              day: '2-digit',
                              year: 'numeric',
                            },
                          )}
                        </div>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          disabled={inviteActionId === invite.id}
                        >
                          <MoreVertical className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onSelect={() => handleResendInvite(invite)}
                          disabled={inviteActionId === invite.id}
                        >
                          <RotateCcw className="h-4 w-4" />
                          {inviteActionId === invite.id &&
                          inviteAction === 'resend'
                            ? 'Resending...'
                            : 'Resend'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          variant="destructive"
                          onSelect={() => handleRevokeInvite(invite)}
                          disabled={inviteActionId === invite.id}
                        >
                          <Trash2 className="h-4 w-4" />
                          {inviteActionId === invite.id &&
                          inviteAction === 'revoke'
                            ? 'Revoking...'
                            : 'Revoke'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
                {invitesLoading && (
                  <div className="p-6 text-xs text-muted-foreground">
                    Loading invites...
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
